<template>
  <div class="hardware my-10 px-2">
    <div class="d-flex justify-space-between" :style="{ position: 'relative' }">
      <!-- left -->
      <div class="d-flex">
        <!-- new record -->
        <div class="text-center">
          <span class="overline"> New </span>
          <div class="mb-4">
            <v-btn
              :min-width="laptop ? '40px' : '42px'"
              height="38px"
              class="elevation-2 px-0"
              color="secondary"
              @click="handleForm({})"
            >
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- form -->
        <v-dialog
          max-width="1200"
          :value="hardwareModal"
          @click:outside="hardwareModal = false"
          @keydown.esc="hardwareModal = false"
        >
          <CueHardwareForm
            :categories="categories"
            :employees="employees"
            :itemToEdit="itemToEdit"
            @hardware-table-refresh="handleRefresh"
          />
        </v-dialog>

        <!-- clear filters -->
        <div class="text-center ml-2">
          <span class="overline"> Clear </span>
          <div class="mb-4">
            <v-btn
              :min-width="laptop ? '40px' : '42px'"
              height="38px"
              class="elevation-2 px-0"
              color="secondary"
              @click="clearFilters"
            >
              <v-icon>mdi-filter-remove</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- user filter -->
        <div class="ml-lg-3 ml-md-2 text-center">
          <span class="overline"> User </span>
          <div class="d-flex mb-4">
            <v-autocomplete
              class="selector"
              :items="employees"
              v-model="filterUser"
              solo
              dense
              hide-details
              :loading="dropdownsLoading"
              @input="handleFilterQuery('User', $event)"
            ></v-autocomplete>
          </div>
        </div>

        <!-- user location filter -->
        <div class="ml-lg-3 ml-md-2 text-center">
          <span class="overline"> User Location </span>
          <div class="d-flex align-end">
            <v-autocomplete
              class="selector"
              :items="countries"
              v-model="filterUserLocation"
              solo
              dense
              hide-details
              :loading="dropdownsLoading"
              @change="handleFilterQuery('UserLocation', $event)"
            ></v-autocomplete>
          </div>
        </div>

        <!-- active user filter -->
        <div class="ml-lg-3 ml-md-2 text-center">
          <span class="overline"> User Activity </span>
          <div>
            <v-btn-toggle
              class="elevation-2"
              color="accent"
              multiple
              dense
              v-model="filterActiveUsers"
              @change="handleFilterQuery('ActiveUsers', $event)"
            >
              <v-btn height="38px">Active</v-btn>
              <v-btn height="38px">Inactive</v-btn>
            </v-btn-toggle>
          </div>
        </div>

        <!-- device category filter -->
        <transition>
          <div
            v-show="laptop ? !expandMenu : true"
            class="ml-lg-3 ml-md-2 text-center"
          >
            <span class="overline"> Device Category </span>
            <div class="d-flex align-end">
              <v-autocomplete
                class="selector"
                :items="categories"
                v-model="filterCategory"
                solo
                dense
                hide-details
                :loading="dropdownsLoading"
                @change="handleFilterQuery('Category', $event)"
              ></v-autocomplete>
            </div>
          </div>
        </transition>

        <!-- on hand filter -->
        <transition>
          <div v-show="!expandMenu" class="ml-lg-3 ml-md-2 text-center">
            <span class="overline"> Status </span>
            <div>
              <v-btn-toggle
                class="elevation-2"
                color="accent"
                multiple
                dense
                v-model="filterOnHand"
                @change="handleFilterQuery('OnHand', $event)"
              >
                <v-btn height="38px">On Hand</v-btn>
                <v-btn height="38px">Free</v-btn>
              </v-btn-toggle>
            </div>
          </div>
        </transition>
      </div>

      <!-- right -->
      <div class="d-flex flex-column justify-start align-end my-0">
        <!-- search -->
        <span class="overline" :style="{ opacity: 0, userSelect: 'none' }"
          >Search</span
        >
        <div class="mb-4">
          <v-badge
            :content="laptopSmaller ? '...' : filterSearchBadge"
            :value="filterSearch && !expandMenu"
            color="secondary"
            overlap
          >
            <v-btn
              height="38px"
              min-width="48px"
              class="elevation-2 px-0"
              outlined
              color="accent"
              @click="expandMenu = !expandMenu"
            >
              <v-icon v-if="expandMenu">mdi-magnify-minus-outline</v-icon>
              <v-icon v-else>mdi-magnify-plus-outline</v-icon>
            </v-btn>
          </v-badge>
          <v-expand-x-transition>
            <v-text-field
              :style="{ position: 'absolute', bottom: '16px', right: '60px' }"
              v-show="expandMenu"
              v-model="filterSearch"
              class="hardware__search-selector"
              solo
              dense
              hide-details
              clearable
            />
          </v-expand-x-transition>
        </div>
      </div>
    </div>
    <!-- table -->
    <v-card>
      <v-skeleton-loader
        v-if="showTableLoader"
        type="table"
        height="100vh"
      ></v-skeleton-loader>
      <CueHardwareTable
        v-if="tableData && !showTableLoader"
        :tableData="tableData"
        :tableFields="tableFields"
        :filterSearch="filterSearch"
        :filterCategory="filterCategory ? filterCategory : ''"
        :filterUser="filterUser ? filterUser : ''"
        :filterUserLocation="filterUserLocation ? filterUserLocation : ''"
        :filterActiveUsers="filterActiveUsers"
        :filterOnHand="filterOnHand"
        @call-edit-form="handleForm"
      />
    </v-card>
    <!-- flash message -->
    <v-snackbar
      v-model="confirmationMsg"
      min-width="unset"
      timeout="2000"
      :color="success ? 'success' : 'error'"
      rounded="pill"
      content-class="text-center"
    >
      <v-icon color="white" class="mr-1">{{
        success ? "mdi-check-circle-outline" : "mdi-close-circle-outline"
      }}</v-icon>
      {{ confirmationMsgTxt }}
    </v-snackbar>
  </div>
</template>

<script>
// libraries
import _ from "lodash";
// internal
import {
  appIDs,
  getHardware,
  getFieldInfosFromDB,
  getEmployeesData,
} from "@/utils/quickbaseUtils";
import { customBreakpoint } from "@/utils/mixins";
import { ROUTES } from "@/utils/constants";
// components
import CueHardwareTable from "@/components/CueHardware/CueHardwareTable";
import CueHardwareForm from "@/components/CueHardware/CueHardwareForm";

export default {
  name: "CueHardware",
  components: {
    CueHardwareTable,
    CueHardwareForm,
  },
  data() {
    return {
      showTableLoader: false,
      tableData: null,
      tableFields: null,
      filterSearch: "",
      expandMenu: false,
      dropdownsLoading: true,
      filterActiveUsers: [0, 1],
      filterOnHand: [0, 1],
      employees: [],
      filterUser: "",
      categories: [],
      filterCategory: "",
      countries: [],
      filterUserLocation: "",
      hardwareModal: false,
      itemToEdit: {},
      success: true,
      confirmationMsg: false,
      confirmationMsgTxt: "",
    };
  },
  computed: {
    filterSearchBadge() {
      return this.filterSearch?.length <= 6
        ? this.filterSearch
        : `${this.filterSearch?.slice(0, 6)}...`;
    },
  },
  mixins: [customBreakpoint],
  watch: {
    filterSearch(newValue) {
      const currentQuery = this.$route.query;
      const newQuery =
        newValue !== ""
          ? {
              ...currentQuery,
              Search: newValue,
            }
          : _.omit(currentQuery, ["Search"]);

      this.$router
        .push({ path: `/${ROUTES.cueHardware}`, query: newQuery })
        .catch(() => {}); // Avoided redundant navigation error handler
    },
  },
  mounted() {
    this.handleQuery();
    this.getTableData();
    this.handleDropdowns();
  },
  methods: {
    getTableData() {
      this.showTableLoader = true;
      getHardware()
        .then((resp) => {
          this.tableData = resp.data;
          this.tableFields = resp.fields;
          this.showTableLoader = false;
        })
        .catch((err) => {
          console.error(err);
          this.showTableLoader = false;
        });
    },
    handleFilterQuery(param, data) {
      const currentQuery = this.$route.query;

      const newQuery = {
        ...currentQuery,
        [param]: data,
      };
      this.$router
        .push({ path: `/${ROUTES.cueHardware}`, query: newQuery })
        .catch(() => {}); // Avoided redundant navigation error handler

      this[`filter${param}`] = data;
    },
    clearFilters() {
      this.filterActiveUsers = this.filterOnHand = [0, 1];
      this.filterUser =
        this.filterUserLocation =
        this.filterCategory =
        this.filterSearch =
          "";

      this.$router.push({ path: `/${ROUTES.cueHardware}` }).catch(() => {}); // Avoided redundant navigation error handler
    },
    getCategories() {
      return new Promise((resolve, reject) => {
        getFieldInfosFromDB({
          fieldID: 7,
          tableID: appIDs.cueProjectTools.hardware,
        })
          .then((categories) => resolve((this.categories = categories)))
          .catch((err) => reject(console.error(err)));
      });
    },
    getEmployees() {
      return new Promise((resolve, reject) => {
        getEmployeesData({ namesOnly: true })
          .then((names) => resolve((this.employees = names)))
          .catch((err) => reject(console.error(err)));
      });
    },
    getContries() {
      return new Promise((resolve, reject) => {
        getFieldInfosFromDB({
          fieldID: 17,
          tableID: appIDs.cueStaff.contacts,
        })
          .then((countries) => resolve((this.countries = countries)))
          .catch((err) => reject(console.error(err)));
      });
    },
    handleDropdowns() {
      Promise.all([
        this.getCategories(),
        this.getEmployees(),
        this.getContries(),
      ])
        .then(() => (this.dropdownsLoading = false))
        .catch((err) => {
          this.dropdownsLoading = false;
          console.error(err);
        });
    },
    handleQuery() {
      const currentQuery = this.$route.query;

      if (!_.isEmpty(currentQuery))
        Object.keys(currentQuery).forEach((param) => {
          if (param === "ActiveUsers" || param === "OnHand") {
            this[`filter${param}`] =
              typeof currentQuery[param] === "string"
                ? [Number(currentQuery[param])]
                : currentQuery[param].map((x) => Number(x));
          } else {
            this[`filter${param}`] = currentQuery[param];
          }
        });
    },
    handleForm(tableRow) {
      this.itemToEdit = { ...tableRow };
      this.hardwareModal = true;
    },
    handleRefresh(msg) {
      this.hardwareModal = false;
      this.getTableData();
      this.success = !!msg;
      this.confirmationMsgTxt =
        msg ?? "Something went wrong. Please try again.";
      this.confirmationMsg = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.hardware {
  max-width: 1500px;
  margin: 0 auto;

  &__search-selector {
    width: 340px;
  }
}

::v-deep {
  .selector {
    width: 200px;
  }
  .selector.v-text-field.v-text-field--enclosed:not(.v-text-field--rounded)
    > .v-input__control
    > .v-input__slot {
    padding-right: 0;
  }
  .selector.v-text-field.v-text-field--solo .v-input__append-inner {
    padding-left: 0;
  }
  .selector.v-autocomplete.v-select.v-input--is-focused input {
    min-width: 0;
  }
  .selector.v-text-field.v-input--dense:not(.v-text-field--outlined) input {
    padding: 2px 0;
  }
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}
.v-enter,
.v-leave-to {
  opacity: 0;
}

@import "~vuetify/src/styles/settings/_variables";

@media #{map-get($display-breakpoints, 'lg-and-down')} {
  .v-application .overline {
    font-size: 0.6rem !important;
  }
}
</style>
